import { BranchHashCell } from "@/components/table/branchHash";
import { TrainingSessionRefLink } from "../trainingRefs";
import type { ReactNode } from "react";
import {
  configLine,
  isPooledSeriesKey,
  type CompareRun,
  type EvalResultSeries,
} from "./evalCompareModel";
import type { CompareDiffRow } from "./evalCompareDiffModel";
import { SeriesSwatch } from "./SeriesSwatch";
import styles from "./CompareTable.module.css";

// Summary·Policy·Scenario 비교 표가 함께 쓰는 셀과 행 모델이다.
export type CompareTableColumn = {
  key: string;
  header: ReactNode;
};

export type CompareTableCell = {
  value: ReactNode | null;
  highlight?: boolean;
  muted?: boolean;
  title?: string;
  missingLabel?: ReactNode;
};

export type CompareTableRow = {
  key: string;
  group?: ReactNode;
  groupKey?: string;
  groupMuted?: boolean;
  label: ReactNode;
  labelMuted?: boolean;
  cells: CompareTableCell[];
};

// Policy·Scenario 값 중 다른 값은 강조하고 같은 값은 흐리게 표시한다.
export function diffTableRow(
  row: CompareDiffRow,
  group: string,
  label: ReactNode,
): CompareTableRow {
  return {
    key: row.key,
    group,
    groupKey: group,
    groupMuted: !row.differs,
    label,
    labelMuted: !row.differs,
    cells: row.values.map((value, index) => ({
      // 비교는 전체 정밀도 값으로 한다. 셀은 대신 축약 표기를 표시할 수 있다.
      value: value !== null ? (row.display?.[index] ?? value) : null,
      highlight: value !== null && row.differs,
      muted: !row.differs,
      title: row.titles?.[index] ?? undefined,
    })),
  };
}

function seriesHeaderContent(
  item: EvalResultSeries,
  showSublabel: boolean,
  showConfig: boolean,
): ReactNode {
  const main =
    item.baseline !== undefined ? (
      <>
        <SeriesSwatch item={item} />
        {/* 머리는 run 상세가 아니라 baseline 을 만든 schedule session 창을 연다. */}
        <TrainingSessionRefLink
          sessionId={item.baseline.sessionId}
          displayName="Baseline"
          sessionName={item.baseline.sessionName ?? undefined}
        />
      </>
    ) : (
      <>
        <SeriesSwatch item={item} />
        <span className={styles.runName} title={item.label}>
          {item.label}
        </span>
      </>
    );
  // 표의 모든 열이 이름 바로 아래 같은 자리에 config 를 보인다. 모르는 열은 "-" 로 자리를 지켜
  // 아래 줄이 열끼리 어긋나지 않는다. 좁은 열에서는 말줄임이 이름을 가린다. title 로 전체 문구를 보인다.
  const config = configLine(item.config);
  const configRow = showConfig ? (
    <span className={styles.runSubline} title={config ?? "config 정보 없음"}>
      {config ?? "-"}
    </span>
  ) : null;
  const sublines =
    item.baseline !== undefined ? (
      <>
        <BranchHashCell branch="main" hash={item.baseline.hash} />
        <span className={styles.runSubline}>Seed {item.baseline.seedCount}개 병합</span>
      </>
    ) : showSublabel ? (
      <span className={styles.runSubline}>{item.sublabel}</span>
    ) : null;
  if (configRow === null && sublines === null) return main;
  return (
    <span className={styles.runHeaderStack}>
      <span className={styles.runHeaderMain}>{main}</span>
      {configRow}
      {sublines}
    </span>
  );
}

export type ResultSeriesColumnOptions = {
  // 열 머리에 시리즈 sublabel 을 부제로 표시한다. 정책 비교처럼 이름과 색이 같은 run 열이
  // 나란히 있는 표에서 켠다. 그런 표에서는 이름과 스와치만으로 열을 구분할 수 없다.
  showSublabel?: boolean;
};

// 열 머리는 색 스와치와 정책 이름을 표시한다. 사람이 고르고 읽는 단위가 정책이기 때문이다.
// 폭이 좁으면 이름을 자르고, hover 하면 전체 이름을 보여 준다. 이름이 같은 run 은 스와치 색으로
// 구분한다.
//
// onOpenRun 을 받으면 열 머리는 그 run 의 상세 창을 여는 링크가 된다. 모양은 baseline 머리의
// session 링크와 같다. 병합 열과 그룹 열은 pooled 결과라서 열 상세가 없다. 그래서 정적으로 둔다.
// 버튼에도 열 폭 상한을 정하는 .runHeader 를 함께 적용해야 한다.
export function resultSeriesColumns(
  items: readonly EvalResultSeries[],
  onOpenRun?: (runId: string) => void,
  options: ResultSeriesColumnOptions = {},
): CompareTableColumn[] {
  const showSublabel = options.showSublabel ?? false;
  // 한 열이라도 config 를 알면 모든 열에 config 줄을 둔다. 줄 수가 열마다 같아야 나란히 비교된다.
  const showConfig = items.some((item) => configLine(item.config) !== null);
  return items.map((item) => ({
    key: item.key,
    header:
      onOpenRun !== undefined && !isPooledSeriesKey(item.key) ? (
        <button
          type="button"
          className={`${styles.runHeader} ${styles.runHeaderButton}`}
          onClick={() => onOpenRun(item.key)}
        >
          {seriesHeaderContent(item, showSublabel, showConfig)}
        </button>
      ) : (
        <span className={styles.runHeader}>
          {seriesHeaderContent(item, showSublabel, showConfig)}
        </span>
      ),
  }));
}

export function compareRunColumns(
  items: readonly CompareRun[],
  onOpenRun?: (runId: string) => void,
  options: ResultSeriesColumnOptions = {},
): CompareTableColumn[] {
  return resultSeriesColumns(items, onOpenRun, options);
}

type RowGroup = {
  key: string;
  label: ReactNode;
  muted: boolean;
  rows: CompareTableRow[];
};

// groupKey 가 같은 연속 행을 row-span 그룹 하나로 합친다.
function groupedRows(rows: readonly CompareTableRow[]): RowGroup[] {
  const groups: RowGroup[] = [];
  const byKey = new Map<string, RowGroup>();
  for (const row of rows) {
    const key = row.groupKey ?? String(row.group ?? row.key);
    const existing = byKey.get(key);
    if (existing !== undefined) {
      existing.rows.push(row);
      existing.muted &&= row.groupMuted ?? false;
    } else {
      const next = {
        key,
        label: row.group ?? row.label,
        muted: row.groupMuted ?? false,
        rows: [row],
      };
      groups.push(next);
      byKey.set(key, next);
    }
  }
  return groups;
}

// 표 하나가 셀 수천 개를 그린다. 그래서 클래스 문자열은 렌더마다 만들지 않고 미리 합쳐 둔다.
const CELL_TEXT_BEST = `${styles.cellText} ${styles.best}`;
const CELL_TEXT_MUTED = `${styles.cellText} ${styles.mutedText}`;

function CompareCell({ cell }: { cell: CompareTableCell }): ReactNode {
  if (cell.value === null) {
    return <span className={styles.mutedText}>{cell.missingLabel ?? "-"}</span>;
  }
  // 열 폭을 넘는 값만 말줄임한다. 잘린 값은 hover 하면 원본을 보여 준다.
  return (
    <span
      className={cell.highlight ? CELL_TEXT_BEST : cell.muted ? CELL_TEXT_MUTED : styles.cellText}
      title={cell.title ?? (typeof cell.value === "string" ? cell.value : undefined)}
    >
      {cell.value}
    </span>
  );
}

// 열 폭은 표가 정한다. 값 열은 남는 폭을 똑같이 나눠 가진다. 열이 많아지면 열을 줄이지 않고
// 가로로 스크롤한다. 그래서 말줄임은 글자 수가 아니라 실제 폭을 기준으로 적용된다.
//
// 항목 열 폭은 가장 긴 이름이 정한다. 기준은 Scenario 의 `RECORD_INCLUDE_SUCCESSFUL` 이고,
// 나머지 표도 같은 폭을 쓴다. 표마다 폭이 다르면 창을 오갈 때 열 위치가 바뀐다.
const LABEL_COLUMN_WIDTH = "7rem";
const LEAF_COLUMN_WIDTH = "14rem";
const MIN_VALUE_COLUMN_WIDTH = "8rem";

function tableMinWidth(columnCount: number): string {
  return `calc(${LABEL_COLUMN_WIDTH} + ${LEAF_COLUMN_WIDTH} + ${columnCount} * ${MIN_VALUE_COLUMN_WIDTH})`;
}

export function CompareTable({
  groupHeader,
  leafHeader,
  columns,
  rows,
  footer,
}: {
  groupHeader: ReactNode;
  leafHeader: ReactNode;
  columns: readonly CompareTableColumn[];
  rows: readonly CompareTableRow[];
  footer?: ReactNode;
}): ReactNode {
  return (
    <div className={styles.wrap}>
      <table className={styles.table} style={{ minWidth: tableMinWidth(columns.length) }}>
        <colgroup>
          <col style={{ width: LABEL_COLUMN_WIDTH }} />
          <col style={{ width: LEAF_COLUMN_WIDTH }} />
          {columns.map((column) => (
            <col key={column.key} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th scope="col" className={styles.labelHead}>
              {groupHeader}
            </th>
            <th scope="col" className={styles.labelHead}>
              {leafHeader}
            </th>
            {columns.map((column) => (
              <th key={column.key} scope="col" className={styles.valueHead}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groupedRows(rows).map((group) =>
            group.rows.map((row, rowIndex) => (
              <tr key={row.key}>
                {rowIndex === 0 ? (
                  <th
                    scope="rowgroup"
                    rowSpan={group.rows.length}
                    className={`${styles.groupCell} ${group.muted ? styles.mutedText : ""}`}
                    title={typeof group.label === "string" ? group.label : undefined}
                  >
                    {group.label}
                  </th>
                ) : null}
                <th
                  scope="row"
                  className={`${styles.leafCell} ${row.labelMuted ? styles.mutedText : ""}`}
                  title={typeof row.label === "string" ? row.label : undefined}
                >
                  {row.label}
                </th>
                {row.cells.map((cell, index) => (
                  <td key={columns[index].key} className={styles.valueCell}>
                    <CompareCell cell={cell} />
                  </td>
                ))}
              </tr>
            )),
          )}
        </tbody>
      </table>
      {footer}
    </div>
  );
}
